import { createContext, type PropsWithChildren, type RefCallback, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRealmAuthContext } from '@interxfi/api-client';
import { useSetUserPreferences } from '@interxfi/api-client/userauth/set-user-preferences';
import { useBreakpoint } from '@interxfi/design-system/util/responsive';
import { noop } from '@interxfi/utilities/fn/no-op';

interface ILayoutContext {
  collapsedSidebarStatus: 'expanded' | 'expanding' | 'collapsing' | 'collapsed';
  handleCloseMobileNav: VoidFunction;
  isMobile: boolean;
  isMobileNavExpanded: boolean;
  isCompact: boolean;
  onSidebarCollapseChange: (newCollapsed: boolean) => void;
  setIsMobileNavExpanded: (newExpanded: boolean) => void;
  setSidebarRef: RefCallback<HTMLElement>;
  showCollapsedSidebar: boolean;
}

const LayoutContext = createContext<ILayoutContext>({
  collapsedSidebarStatus: 'expanded',
  handleCloseMobileNav: noop,
  isCompact: false,
  isMobile: false,
  isMobileNavExpanded: false,
  onSidebarCollapseChange: noop,
  setIsMobileNavExpanded: noop,
  setSidebarRef: noop,
  showCollapsedSidebar: false,
});

export function LayoutContextProvider({ children }: PropsWithChildren) {
  const isCompact = useBreakpoint('md');
  const isMobile = useBreakpoint('sm');
  const [isMobileNavExpanded, setIsMobileNavExpanded] = useState(false);
  const authContext = useRealmAuthContext();
  const [sidebarStatus, setSidebarStatus] = useState<ILayoutContext['collapsedSidebarStatus']>(
    isCompact || (authContext?.data?.preferences !== undefined && !authContext.data.preferences.prefersExpandedNavigation) ? 'collapsed' : 'expanded',
  );
  const hasInitializedCollapsePreference = useRef(false);
  const [sidebarRef, setSidebarRef] = useState<HTMLElement | null>(null);
  const { mutate } = useSetUserPreferences();

  const onSidebarCollapseChange = useCallback(
    (newCollapsed: boolean) => {
      setSidebarStatus(newCollapsed ? 'collapsing' : 'expanding');

      // Per design requirements, only store the user preference change if not in the compact view
      if (authContext?.userId && !isCompact) {
        mutate({
          userId: authContext.userId,
          preferences: {
            ...authContext?.data?.preferences,
            prefersExpandedNavigation: !newCollapsed,
          },
        });
      }
    },
    [authContext?.data?.preferences, authContext?.userId, mutate, isCompact],
  );

  useEffect(() => {
    if (!isMobile) {
      setIsMobileNavExpanded(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.currentTarget === sidebarRef && e.propertyName === 'width') {
        setSidebarStatus((prevState) => {
          switch (prevState) {
            case 'collapsing':
              return 'collapsed';
            case 'expanding':
              return 'expanded';
            default:
              return prevState;
          }
        });
      }
    };

    sidebarRef?.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      sidebarRef?.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [sidebarRef]);

  const hasOptions = Boolean(authContext?.data?.preferences);

  useEffect(() => {
    if (isCompact) {
      hasInitializedCollapsePreference.current = true;
      setSidebarStatus('collapsed');
      return;
    }

    setSidebarStatus('expanded');
  }, [isCompact]);

  useEffect(() => {
    if (isCompact) {
      return;
    }

    if (hasOptions && !hasInitializedCollapsePreference.current) {
      hasInitializedCollapsePreference.current = true;
      setSidebarStatus(authContext?.data.preferences?.prefersExpandedNavigation ? 'expanded' : 'collapsed');
    }
  }, [authContext?.data?.preferences?.prefersExpandedNavigation, hasOptions, isCompact]);

  const handleCloseMobileNav = useCallback(() => {
    setIsMobileNavExpanded(false);
  }, []);

  const value = useMemo<ILayoutContext>(
    () => ({
      collapsedSidebarStatus: sidebarStatus,
      handleCloseMobileNav,
      isCompact,
      isMobile,
      isMobileNavExpanded,
      onSidebarCollapseChange,
      setIsMobileNavExpanded,
      setSidebarRef,
      showCollapsedSidebar: !isMobile && (sidebarStatus === 'collapsed' || sidebarStatus === 'collapsing'),
    }),
    [isCompact, isMobile, onSidebarCollapseChange, sidebarStatus, isMobileNavExpanded, handleCloseMobileNav],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayoutContext() {
  return use(LayoutContext);
}
